import { LoadingOverlay } from '@mantine/core'
import { useRouter } from 'next/router'
import React from 'react'

import { Redirect, ViewError } from '@/components/commons/'
import { useFetch } from '@/lib/hooks'
import { getUrlString, isEmailConfirmationRequired } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { SiteData as SiteDataType } from '@/types'

interface Props {
  initialData?: SiteDataType
  children?: React.ReactNode
}

export default function UnrestrictedSiteGuard({ initialData, children }: Props) {
  // Hooks
  const router = useRouter()
  const { isAuthenticated, isEmailConfirmed } = useAuth() || {}

  // Constants
  const { callbackUrl, site: siteSlug } = router.query || {}
  const callbackUrlSlug = getUrlString(callbackUrl)

  // Fetch
  const { data: siteData, error } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site // rehydration if necessary
  })

  const isLoading = !siteData && !error

  // Render
  if (isLoading) return <LoadingOverlay visible /> // Loading

  if (siteData?.maintenance) return <Redirect url="/maintenance" replace />

  if (error) {
    return (
      <ViewError
        title="Erro"
        message="Ops... Houve um erro ao carregar esta página. Tente novamente ou entre em contato conosco."
      />
    ) // Error view if initial data and retrieve failed
  }

  // TEMP: após o cadastro, redireciona para complete-profile (dados + alunos)
  if (isAuthenticated && router.asPath.startsWith('/accounts/signup')) {
    return (
      <Redirect
        url="/accounts/complete-profile"
        callbackUrl={callbackUrlSlug ? encodeURIComponent(callbackUrlSlug) : undefined}
        replace
      />
    )
  }

  if (isAuthenticated) return <Redirect url={callbackUrlSlug || '/accounts/'} replace />

  if (isEmailConfirmationRequired(siteData?.requireEmailConfirmation, isEmailConfirmed)) {
    return <Redirect url="/accounts/confirm-email" replace />
  }

  return <>{children}</>
}
